import React, { useCallback, useMemo } from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import cn from 'classnames';
import { max } from 'date-fns';

import {
	Button,
	Datepicker,
	FoldPanel,
	Label,
	ModalSelect,
	RadioButton,
	Select,
} from '@components/common';

import { startOfDayInMilliseconds } from '@utils/date';
import { fieldsOptionsFormatted } from '@utils/formatter';
import { endOfDayInUTC, formatDate, getDateWithoutOffset } from '@utils/kpi-utils/date';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ITreePreset } from '@interfaces/components/tree';
import { IModalSelectOption } from '@components/common/ModalSelect/ModalSelect.types';

import { EParametersFormValues } from '../enums';

import { formatTemplateDefault } from '@constants/common';

import styles from './ComparisonOptions.module.scss';

interface IComparisonOptionsProps {
	isOpenComparisonOptions: boolean;
	toggleComparisonOptions: () => void;
	nodesOptions: Array<TSelectOption<string>>;
	releaseVersionsOptions: Array<TSelectOption<string>>;
	configTypesOptions: Array<TSelectOption<string>>;
	technologiesOptions: Array<TSelectOption>;
	nodeTypesOptions: Array<TSelectOption>;
	siteIdsOptions: Array<IModalSelectOption>;
	datesOptions: Array<number>;
	errors: FieldErrors;
	control: Control;
	watch: UseFormWatch<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	register: UseFormRegister<FieldValues>;
	handleSubmit: () => void;
	isDisabledSubmit: boolean;
	isLoading: boolean;
	selectedPreset: TSelectOption | null;
	savedPreset?: ITreePreset;
	isMavenirVendor: boolean;
	datesMavenirOptions: Date[];
}

const filterDates = (dates: Date[], selected: Date[]): Date[] => {
	return dates.filter((date) => {
		return !selected.some(selectedDate => endOfDayInUTC(date) === endOfDayInUTC(selectedDate));
	});
};

const ComparisonOptions = ({
	isOpenComparisonOptions,
	toggleComparisonOptions,
	nodesOptions,
	technologiesOptions,
	nodeTypesOptions,
	siteIdsOptions,
	datesOptions,
	errors,
	control,
	handleSubmit,
	watch,
	setValue,
	register,
	isDisabledSubmit,
	isLoading,
	selectedPreset,
	savedPreset,
	isMavenirVendor,
	releaseVersionsOptions,
	configTypesOptions,
	datesMavenirOptions,
}: IComparisonOptionsProps) => {
	const disabledDate = datesOptions?.length || datesMavenirOptions?.length
		? isMavenirVendor
			? !watch(EParametersFormValues.type)
			: !watch(EParametersFormValues.sites).length
		: true;

	const isDisabledIsFirstCurrentDate = watch(EParametersFormValues.isSecondCurrentDate)
		|| disabledDate;
	const isDisabledIsSecondCurrentDate = watch(EParametersFormValues.isFirstCurrentDate)
		|| disabledDate;
	const isDisabledDataSourceCreatedAtFirst = watch(EParametersFormValues.isFirstCurrentDate)
		|| disabledDate
		|| !!selectedPreset;
	const isDisabledDataSourceCreatedAtSecond = watch(EParametersFormValues.isSecondCurrentDate)
		|| disabledDate
		|| !!selectedPreset;

	const dataSourceCreatedAtFirst = watch(EParametersFormValues.dataSourceCreatedAtFirst);
	const dataSourceCreatedAtSecond = watch(EParametersFormValues.dataSourceCreatedAtSecond);
	const isFirstCurrentDate = watch(EParametersFormValues.isFirstCurrentDate);
	const isSecondCurrentDate = watch(EParametersFormValues.isSecondCurrentDate);
	const nodeValue = selectedPreset ? [] : watch(EParametersFormValues.nodes);

	const selectedSitesPresetOptions = useMemo(() => {
		return savedPreset?.siteIds ? fieldsOptionsFormatted(savedPreset.siteIds) : [];
	}, [savedPreset]);

	const onChangeSites = (values: Array<IModalSelectOption>) => setValue('sites', values);

	const onChangeNodes = (values: Array<IModalSelectOption>) => setValue('nodes', values);

	const setDataSourceCreatedAtFirst = (date: number | null) => setValue('dataSourceCreatedAtFirst', date);

	const setDataSourceCreatedAtSecond = (date: number | null) => setValue('dataSourceCreatedAtSecond', date);

	const datesOptionsFormatted = useMemo(() => {
		return isMavenirVendor ? datesMavenirOptions : datesOptions.map((date) => getDateWithoutOffset(date));
	}, [datesMavenirOptions, datesOptions, isMavenirVendor]);

	const filteredDatesAtFirst = useMemo(() => {
		const selectedDates = [new Date(dataSourceCreatedAtSecond)];

		if (isSecondCurrentDate) {
			selectedDates.push(new Date(max(datesOptionsFormatted)));
		}
		return filterDates(datesOptionsFormatted, selectedDates);
	}, [dataSourceCreatedAtSecond, isSecondCurrentDate, datesOptionsFormatted]);

	const filteredDatesAtSecond = useMemo(() => {
		const selectedDates = [new Date(dataSourceCreatedAtFirst)];

		if (isFirstCurrentDate) {
			selectedDates.push(new Date(max(datesOptionsFormatted)));
		}

		return filterDates(datesOptionsFormatted, selectedDates);
	}, [datesOptionsFormatted, dataSourceCreatedAtFirst, isFirstCurrentDate]);

	const excludedDatesOptions = useMemo(() => datesOptionsFormatted.filter((date) => {
		const currentDate = startOfDayInMilliseconds(date);

		return currentDate !== dataSourceCreatedAtFirst && currentDate !== dataSourceCreatedAtSecond;
	}), [datesOptionsFormatted, dataSourceCreatedAtFirst, dataSourceCreatedAtSecond]);

	const currentParametersDate = datesOptionsFormatted.length && excludedDatesOptions.length
		? formatDate(max(excludedDatesOptions), formatTemplateDefault)
		: null;

	const toggleCurrentDate = useCallback((fieldName: string, datepickerName: string) => {
		return (value: boolean) => {
			setValue(datepickerName, null);
			setValue(fieldName, value);
		};
	}, [setValue]);

	const toggleCreatedAtFirst = useMemo(() => {
		return toggleCurrentDate(
			EParametersFormValues.isFirstCurrentDate,
			EParametersFormValues.dataSourceCreatedAtFirst,
		);
	}, [toggleCurrentDate]);

	const toggleCreatedAtSecond = useMemo(() => {
		return toggleCurrentDate(
			EParametersFormValues.isSecondCurrentDate,
			EParametersFormValues.dataSourceCreatedAtSecond,
		);
	}, [toggleCurrentDate]);

	return (
		<FoldPanel
			buttonsComponent={
				<div className={styles.comparisonOptionsButton}>
					<Button
						disabled={isDisabledSubmit}
						large
						onClick={handleSubmit}
						text='Apply MO Tree'
					/>
				</div>
			}
			isLoading={isLoading}
			isOpen={isOpenComparisonOptions}
			title='Comparison Options'
			togglePanel={toggleComparisonOptions}
			tooltip='Comparison Options'
			width='400px'
		>
			{isMavenirVendor
				&& <div className={styles.subTitle}>
					5G RAN Config
				</div>
			}
			<div className={styles.groupOptions}>
				{!isMavenirVendor
					&& <>
						<Select
							control={control}
							disabled={!watch(EParametersFormValues.vendor)}
							errorMessage={errors.technology?.message}
							isError={!!errors?.technology}
							label='Technology'
							name={EParametersFormValues.technology}
							options={technologiesOptions}
							placeholder='Select technology'
							required
						/>
						<Select
							control={control}
							disabled={!watch(EParametersFormValues.technology)}
							errorMessage={errors.nodeType?.message}
							isError={!!errors?.nodeType}
							label='Node type'
							name={EParametersFormValues.nodeType}
							options={nodeTypesOptions}
							placeholder='Select node type'
							required
						/>
					</>
				}
			</div>
			{!isMavenirVendor
				&& <div>
					<Label
						className={cn(styles.boldLabel, styles.pb5)}
						label='Sites'
						required
					/>
					<ModalSelect
						disabled={!watch(EParametersFormValues.nodeType) || !siteIdsOptions.length}
						label='Select sites'
						modalLabel='Select sites'
						onChange={onChangeSites}
						options={siteIdsOptions}
						searchPlaceholder='Search by site ID'
						values={selectedPreset ? [] : watch(EParametersFormValues.sites)}
					/>
				</div>
			}
			{isMavenirVendor
				&& <>
					<div>
						<Label
							className={cn(styles.boldLabel, styles.pb5)}
							label='Nodes'
							required
						/>
						<ModalSelect
							disabled={!watch(EParametersFormValues.vendor) || !nodesOptions.length}
							label='Select nodes'
							modalLabel='Select nodes'
							onChange={onChangeNodes}
							options={nodesOptions}
							searchPlaceholder='Search by node ID'
							values={nodeValue}
						/>
					</div>
					<div className={styles.groupOptions}>
						<Select
							control={control}
							disabled={!watch(EParametersFormValues.nodes).length}
							errorMessage={errors.version?.message}
							isError={!!errors?.version}
							label='Release version'
							name={EParametersFormValues.version}
							options={releaseVersionsOptions}
							placeholder='Select release version'
							required
						/>
						<Select
							control={control}
							disabled={!watch(EParametersFormValues.version)}
							errorMessage={errors.type?.message}
							isError={!!errors?.type}
							label='Config type'
							name={EParametersFormValues.type}
							options={configTypesOptions}
							placeholder='Select config type'
							required
						/>
					</div>
				</>
			}
			{selectedPreset
				? <div>
					<div className={styles.historyText}>
						Configuration management history:
					</div>
					<ModalSelect
						customButton={
							<div className={styles.historySites}>
								<span>Selected sites</span> ({selectedSitesPresetOptions.length})
							</div>
						}
						isViewMode
						label='Selected sites'
						modalLabel='Selected sites'
						options={selectedSitesPresetOptions}
						searchPlaceholder='Search by site ID'
					/>
				</div>
				: <div className={styles.groupOptions}>
					<div className={styles.boldLabel}>
						Data source 1 <span className={styles.asterisk}>*</span>:
					</div>
					<div className={styles.radioWrapper}>
						<RadioButton
							key='isFirstCurrentDate'
							checked={isFirstCurrentDate}
							disabled={isDisabledIsFirstCurrentDate}
							id='isFirstCurrentDate'
							label='Current parameters'
							name={EParametersFormValues.isFirstCurrentDate}
							register={register}
							setValue={toggleCreatedAtFirst}
							value
							withUseForms
						/>
						{isFirstCurrentDate
							&& <div className={styles.currentParametersDate}>
								{currentParametersDate}
							</div>
						}
					</div>
				</div>
			}
			<div className={styles.datepickerWrapper}>
				<div>
					{selectedPreset
						? <span>Data source 1</span>
						: <RadioButton
							key='firstDataSource'
							checked={!watch(EParametersFormValues.isFirstCurrentDate)}
							disabled={disabledDate}
							id='firstDataSource'
							label='Date'
							name={EParametersFormValues.isFirstCurrentDate}
							register={register}
							setValue={setValue}
							value={false}
							withUseForms
						/>
					}
				</div>
				<Datepicker
					disableInput
					disabled={isDisabledDataSourceCreatedAtFirst}
					handleChange={setDataSourceCreatedAtFirst}
					includeDates={filteredDatesAtFirst}
					isMobileDevice={false}
					isTodayButton={false}
					selected={watch(EParametersFormValues.dataSourceCreatedAtFirst)}
				/>
			</div>
			{!selectedPreset
				&& <div className={styles.groupOptions}>
					<div className={styles.boldLabel}>
						Data source 2 <span className={styles.asterisk}>*</span>:
					</div>
					<div className={styles.radioWrapper}>
						<RadioButton
							key='isSecondCurrentDate'
							checked={isSecondCurrentDate}
							disabled={isDisabledIsSecondCurrentDate}
							id='isSecondCurrentDate'
							label='Current parameters'
							name={EParametersFormValues.isSecondCurrentDate}
							register={register}
							setValue={toggleCreatedAtSecond}
							value
							withUseForms
						/>
						{isSecondCurrentDate
							&& <div className={styles.currentParametersDate}>
								{currentParametersDate}
							</div>
						}
					</div>
				</div>
			}
			<div className={styles.datepickerWrapper}>
				<div>
					{selectedPreset
						? <span>Data source 2</span>
						: <RadioButton
							key='secondDataSource'
							checked={!watch(EParametersFormValues.isSecondCurrentDate)}
							disabled={disabledDate}
							id='secondDataSource'
							label='Date'
							name={EParametersFormValues.isSecondCurrentDate}
							register={register}
							setValue={setValue}
							value={false}
							withUseForms
						/>
					}
				</div>
				<Datepicker
					disableInput
					disabled={isDisabledDataSourceCreatedAtSecond}
					handleChange={setDataSourceCreatedAtSecond}
					includeDates={filteredDatesAtSecond}
					isMobileDevice={false}
					isTodayButton={false}
					selected={watch(EParametersFormValues.dataSourceCreatedAtSecond)}
				/>
			</div>
		</FoldPanel>
	);
};

export default ComparisonOptions;
